import { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";

import { Button, Loader } from "@/components";
import { Image01, SearchMagnifyingGlass } from "@/icons";
import { useTranslate } from "@/providers/context";
import { cn } from "@/utils";

import {
  CommunityFeedSkeleton,
  CommunityPost,
  CommunityPostHeader,
  CommunityPostModal,
} from "../";
import { SEARCH_PARAM } from "../../const";
import { useCommunityFeed } from "../../providers/context/useCommunityFeed";
import { BlockInteractionModal } from "./BlockInteractionModal";

const EmptyState = ({
  isJoined,
  onHeaderClick,
  query,
}: {
  isJoined?: boolean;
  onHeaderClick: () => void;
  query: string | null;
}) => {
  const { translate } = useTranslate();

  if (query) {
    return (
      <div
        className={cn("flex h-full flex-col items-center justify-center gap-4")}
      >
        <SearchMagnifyingGlass height={68} width={68} />
        <div className="p1 w-[264px] text-center text-item-secondary">
          {`${translate("We couldn't find any results with")} "${query}"`}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col items-center justify-center gap-4 rounded-t-xl bg-surface-secondary text-item-tertiary",
        isJoined && "pt-[54px]",
      )}
    >
      <Image01 height={68} width={68} />
      <div className="p1">{translate("No posts yet.")}</div>
      {isJoined && (
        <Button onClick={onHeaderClick}>
          {translate("Create the first post")}
        </Button>
      )}
    </div>
  );
};

interface PostsListProps {
  scrollableTarget: string;
}

export const PostsList = ({ ...props }: PostsListProps) => {
  const { translate } = useTranslate();
  const {
    loadingInitialContent,
    posts,
    pinnedPost,
    hasNextPage,
    loadingNextPage,
    handleNextPage,
    group,
  } = useCommunityFeed();

  const [searchParams] = useSearchParams();

  const query = searchParams.get(SEARCH_PARAM);

  const isJoined = group.isJoined;

  useEffect(() => {
    if (isJoined) {
      setIsBlockInteractionModalOpen(false);
    }
  }, [isJoined]);

  const [isInputModalOpen, setIsInputModalOpen] = useState(false);
  const [isBlockInteractionModalOpen, setIsBlockInteractionModalOpen] =
    useState(false);

  const onHeaderClick = useCallback(() => {
    setIsInputModalOpen(true);
  }, [setIsInputModalOpen]);

  const onEndReached = useCallback(() => {
    if (hasNextPage && !loadingNextPage) {
      handleNextPage();
    }
  }, [hasNextPage, loadingNextPage, handleNextPage]);

  return (
    <>
      <div className="h-full w-[636px]">
        {query && (
          <div className="h4-bold mb-8 pl-3">
            {`${translate("Results for")} "${query}"`}
          </div>
        )}
        {loadingInitialContent ? (
          <div className="h-full">
            <CommunityFeedSkeleton />
          </div>
        ) : posts.length === 0 ? (
          <EmptyState
            isJoined={isJoined}
            onHeaderClick={onHeaderClick}
            query={query}
          />
        ) : (
          <InfiniteScroll
            dataLength={posts.length}
            loader={
              <div className="flex justify-center py-12">
                <Loader className="h-8 w-8 text-item-tertiary" />
              </div>
            }
            endMessage={
              <div className="p1 flex justify-center pb-[72px] pt-[62px] text-item-secondary">
                {translate("No more posts.")}
              </div>
            }
            scrollThreshold={0.7}
            next={onEndReached}
            hasMore={hasNextPage}
            {...props}
          >
            {isJoined && !query && (
              <CommunityPostHeader onClick={onHeaderClick} />
            )}
            {pinnedPost && !query && (
              <CommunityPost
                key={pinnedPost.id}
                post={pinnedPost}
                isJoined={isJoined}
                hideTopBorder={!isJoined}
                onBlockInteraction={() => setIsBlockInteractionModalOpen(true)}
              />
            )}
            {posts
              .filter((post) => post.id !== pinnedPost?.id)
              .map((post, idx) => (
                <CommunityPost
                  key={post.id}
                  post={post}
                  isJoined={isJoined}
                  hideTopBorder={
                    (!!query && idx === 0) ||
                    (!pinnedPost && !isJoined && idx === 0)
                  }
                  onBlockInteraction={() =>
                    setIsBlockInteractionModalOpen(true)
                  }
                />
              ))}
          </InfiniteScroll>
        )}
      </div>
      <CommunityPostModal
        open={isInputModalOpen}
        setOpen={setIsInputModalOpen}
      />
      <BlockInteractionModal
        open={isBlockInteractionModalOpen}
        setOpen={setIsBlockInteractionModalOpen}
      />
    </>
  );
};
